import { type ReactNode } from "react";
import { motion } from "framer-motion";
import { useUIStore } from "../../stores/ui";
import { AmbientBackground } from "./AmbientBackground";
import { TopBar, TOPBAR_HEIGHT } from "./TopBar";
import { Sidebar } from "./Sidebar";
import { SplitPane } from "./SplitPane";
import { BottomPanel } from "./BottomPanel";

interface AppLayoutProps {
  editorSlot: ReactNode;
  previewSlot: ReactNode;
  filesContent?: ReactNode;
  outlineContent?: ReactNode;
  commentsContent?: ReactNode;
  chatContent?: ReactNode;
  citationsContent?: ReactNode;
  contextContent?: ReactNode;
  errorsContent?: ReactNode;
  logsContent?: ReactNode;
  versionsContent?: ReactNode;
  vitalsContent?: ReactNode;

  hidePreview?: boolean;

  documentTitle?: string;
  onTitleChange?: (title: string) => void;
  documentType?: "latex" | "markdown" | "docx";
  isSaved?: boolean;
  errorCount?: number;

  onOpenHistory?: () => void;
  onShare?: () => void;
  onDownloadSource?: () => void;
  onDownloadPdf?: () => void;
  onDownloadDocx?: () => void;
  onPrint?: () => void;
  onOpenShortcuts?: () => void;
  onWordCount?: () => void;
}

const spring = { type: "spring" as const, stiffness: 400, damping: 35, mass: 0.8 };

export function AppLayout({
  editorSlot,
  previewSlot,
  hidePreview,
  filesContent,
  outlineContent,
  commentsContent,
  chatContent,
  citationsContent,
  contextContent,
  errorsContent,
  logsContent,
  versionsContent,
  vitalsContent,
  documentTitle,
  onTitleChange,
  documentType,
  isSaved,
  errorCount,
  onOpenHistory,
  onShare,
  onDownloadSource,
  onDownloadPdf,
  onDownloadDocx,
  onPrint,
  onOpenShortcuts,
  onWordCount,
}: AppLayoutProps) {
  const focusMode = useUIStore((s) => s.focusMode);

  return (
    <div className="relative flex h-dvh w-screen flex-col overflow-hidden bg-[var(--bg-primary)]">
      <AmbientBackground />

      <TopBar
        documentTitle={documentTitle}
        onTitleChange={onTitleChange}
        documentType={documentType}
        isSaved={isSaved}
        onOpenHistory={onOpenHistory}
        onShare={onShare}
        onDownloadSource={onDownloadSource}
        onDownloadPdf={onDownloadPdf}
        onDownloadDocx={onDownloadDocx}
        onPrint={onPrint}
        onOpenShortcuts={onOpenShortcuts}
        onWordCount={onWordCount}
      />

      <div
        className="flex flex-1 min-h-0 overflow-hidden"
        style={{ height: `calc(100dvh - ${TOPBAR_HEIGHT}px)` }}
      >
        <motion.div
          className="h-full shrink-0"
          animate={{ opacity: focusMode ? 0.15 : 1 }}
          transition={spring}
          style={{ pointerEvents: focusMode ? "none" : "auto" }}
        >
          <Sidebar
            filesContent={filesContent}
            outlineContent={outlineContent}
            commentsContent={commentsContent}
            chatContent={chatContent}
            citationsContent={citationsContent}
            contextContent={contextContent}
          />
        </motion.div>

        <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            {hidePreview ? (
              <div className="h-full overflow-hidden">{editorSlot}</div>
            ) : (
              <SplitPane left={editorSlot} right={previewSlot} />
            )}
          </div>

          <motion.div
            className="shrink-0"
            animate={{ opacity: focusMode ? 0.15 : 1 }}
            transition={spring}
            style={{ pointerEvents: focusMode ? "none" : "auto" }}
          >
            <BottomPanel
              errorsContent={errorsContent}
              logsContent={logsContent}
              versionsContent={versionsContent}
              vitalsContent={vitalsContent}
              errorCount={errorCount}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
